import { File, FilePdf, Plus, X } from "@phosphor-icons/react"
import { Button, Spinner, Toast } from "@vesatogo/grass-core"
import axios from "axios"
import { useCallback, useState } from "react"
import { useDropzone } from "react-dropzone"
import toast from "react-hot-toast"
import { v4 as uuid4 } from "uuid"

export type DropzoneProps = {
  endpoint: string
  onChange?: (newArray: any[]) => void
  onError?: (message: string) => void
  onSuccess?: (message: any) => void
  maxFiles?: number
  maxFileSize?: number // in bytes
  multiple?: boolean
  access?: "public-read" | "authenticated-read" | "private"
  shouldEncrypt?: boolean
  className?: string
  disabled?: boolean
  method?: "post" | "put"
  appName?: string
  environment?: string
  accept?: string | Record<string, string[]>
  helperText?: string
  media?: any[]
}

function generateFilename(file: File) {
  const extension = file.name.split(".").pop() || file.type.split("/")[1]
  return `${uuid4()}.${extension}`
}

export const Dropzone = ({
  endpoint,
  onChange,
  onError,
  onSuccess,
  maxFiles = 8,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  multiple = true,
  access = "public-read",
  shouldEncrypt = false,
  className = "",
  disabled = false,
  method = "post",
  appName = "dashboard",
  environment = "",
  accept = {
    "image/*": [".jpeg", ".jpg", ".png", ".webp"],
    "application/pdf": [".pdf"],
  },
  helperText = "Upload multiple PDF, JPG, or PNG files (maximum size: 10 MB)",
  media = [],
}: DropzoneProps) => {
  const [loading, setLoading] = useState(false)

  const uploadFiles = async (files: File[]) => {
    try {
      setLoading(true)
      if (!files?.length) return

      const path = [appName, environment].filter(str => str).join("/") + "/"
      const filesPromises = files.map(file => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("path", path)
        formData.append("access", access)
        formData.append("should_encrypt", shouldEncrypt.toString())
        formData.append("name", generateFilename(file))

        return method === "post"
          ? axios.post(endpoint, formData, {
              headers: {
                authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            })
          : axios.put(endpoint, formData, {
              headers: {
                authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            })
      })

      const uploadedFiles = await Promise.all(filesPromises)
      const newFiles = uploadedFiles.map(({ data }) => {
        const type = /(.jpeg|.jpg|.png|.webp)$/i.test(data.media.url)
          ? "image"
          : "file"

        return {
          ...data?.media,
          is_encrypted: shouldEncrypt,
          media_kind: type,
          access,
        }
      })

      onChange?.([...media, ...newFiles])
      onSuccess?.(uploadedFiles)
    } catch (err: any) {
      const errorMessage = `Something went wrong! ${err.message || err}`
      onError?.(errorMessage)
      toast.custom(<Toast intent="danger" title={errorMessage} />)
    } finally {
      setLoading(false)
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const currentFileCount = media.length

      if (acceptedFiles.length + currentFileCount > maxFiles) {
        const msg = `Cannot upload more than ${maxFiles} files`
        onError?.(msg)
        toast.custom(<Toast intent="danger" title={msg} />)
        return
      }

      // Validate file sizes
      const oversizedFiles = acceptedFiles.filter(
        file => file.size > maxFileSize
      )
      if (oversizedFiles.length > 0) {
        const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0)
        const fileNames = oversizedFiles.map(f => f.name).join(", ")
        const msg = `File(s) exceed ${maxSizeMB}MB limit: ${fileNames}`
        onError?.(msg)
        toast.custom(<Toast intent="danger" title={msg} />, { duration: 5000 })
        return
      }

      uploadFiles(acceptedFiles)
    },
    [media, maxFiles, maxFileSize, uploadFiles, onError]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: typeof accept === "string" ? undefined : accept,
    multiple,
    disabled: disabled || loading,
    noClick: true, // We will handle click manually
  })

  const removeFile = (index: number) => {
    const newMedia = [...media]
    newMedia.splice(index, 1)
    onChange?.(newMedia)
  }

  const getFileExtension = (url: string) => {
    return url.split(".").pop()?.toLowerCase() || ""
  }

  const isImage = (file: any) => {
    const url = file.url || file.media?.url || ""
    const ext = getFileExtension(url)
    return ["jpg", "jpeg", "png", "webp", "gif"].includes(ext)
  }

  const isPDF = (file: any) => {
    const url = file.url || file.media?.url || ""
    const ext = getFileExtension(url)
    return ext === "pdf"
  }

  if (media.length > 0) {
    return (
      <div {...getRootProps()} className={`flex flex-wrap gap-4 ${className}`}>
        <input {...getInputProps()} />
        <div
          onClick={open}
          className={`w-[100px] h-[100px] border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-green-500 transition-colors cursor-pointer bg-gray-50/50 ${
            isDragActive ? "border-green-500 bg-green-50/30" : ""
          }`}
        >
          <Plus size={24} className="text-gray-400" />
          <span className="text-xs font-500 text-gray-600">Upload</span>
        </div>

        {media.map((file, index) => {
          const url = file.url || file.media?.url || ""
          return (
            <div
              key={index}
              onClick={() => window.open(url, "_blank")}
              className="w-[100px] h-[100px] border border-gray-200 rounded-lg bg-gray-100 flex items-center justify-center relative group overflow-hidden cursor-pointer hover:bg-gray-200 transition-colors"
            >
              {isImage(file) ? (
                <img
                  src={url}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center">
                  {isPDF(file) ? (
                    <FilePdf size={40} weight="fill" className="text-red-500" />
                  ) : (
                    <File size={40} weight="fill" className="text-gray-400" />
                  )}
                  <span className="text-[10px] text-gray-500 mt-1 uppercase font-bold">
                    {getFileExtension(url) || "FILE"}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  removeFile(index)
                }}
                className="absolute top-1 right-1 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors cursor-pointer z-10"
              >
                <X size={12} className="text-gray-400" />
              </button>
            </div>
          )
        })}

        {loading && (
          <div className="w-[100px] h-[100px] border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50/50">
            <Spinner size={24} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      {...getRootProps()}
      onClick={open}
      className={`border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center gap-2 bg-gray-50/50 transition-colors cursor-pointer hover:border-green-500 ${
        isDragActive ? "border-green-500 bg-green-50/30" : ""
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-1 text-center">
        {loading ? (
          <Spinner size={32} />
        ) : (
          <>
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
              <Plus size={24} weight="bold" />
            </div>
            <p className="font-600 text-gray-800">
              Click to upload or drag and drop
            </p>
            {helperText && (
              <p className="text-xs text-gray-500 max-w-[200px]">
                {helperText}
              </p>
            )}
          </>
        )}
      </div>
      {!loading && (
        <Button
          type="button"
          variant="outline"
          intent="primary"
          size="sm"
          className="mt-2"
          onClick={e => {
            e.stopPropagation()
            open()
          }}
          leftIcon={<Plus size={16} />}
          disabled={disabled}
        >
          File
        </Button>
      )}
    </div>
  )
}
